import React, { useState } from 'react';
import { motion } from 'framer-motion';
import type { SiteMetric, AnalyticsSummary } from '../types';
import { useDelayedAnimation } from '../hooks/useDelayedAnimation';

interface UptimeDonutProps {
    summary?: AnalyticsSummary;
    sites: SiteMetric[];
    isLoading: boolean;
}

const DONUT_CX = 52;
const DONUT_CY = 52;
const DONUT_R = 38;
const STROKE_W = 10;
const CIRC = 2 * Math.PI * DONUT_R;

const getUptimeColor = (uptime: number): string => {
    if (uptime >= 99.9) return '#10B981';
    if (uptime >= 99) return '#2563EB';
    if (uptime >= 95) return '#F59E0B';
    return '#EF4444';
};

export const UptimeDonut: React.FC<UptimeDonutProps> = ({
    summary,
    sites,
    isLoading,
}) => {
    const [hoveredId, setHoveredId] = useState<string | null>(null);

    const { animated } = useDelayedAnimation(!isLoading && !!summary, {
        delay: 80,
    })

    if (isLoading || !summary) {
        return (
            <div className="bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl p-4 h-[176px] animate-pulse" />
        );
    }

    const overall = summary.overallUptime;
    const overallColor = getUptimeColor(overall);

    const overallDash = animated ? (overall / 100) * CIRC : 0;
    const overallGap = CIRC - overallDash;

    const hoveredSite = hoveredId ? sites.find(s => s.siteId === hoveredId) : null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl p-4"
        >
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-[13px] font-medium text-[var(--text)]">Uptime</h3>
                <span
                    className="text-[11px] font-mono font-semibold tabular-nums"
                    style={{ color: overallColor }}
                >
                    {overall.toFixed(2)}%
                </span>
            </div>

            <div className="flex items-center gap-4">
                <div className="relative flex-shrink-0">
                    <svg
                        width={104}
                        height={104}
                        viewBox="0 0 104 104"
                        aria-label={`Общий uptime: ${overall.toFixed(2)}%`}
                    >
                        <circle
                            cx={DONUT_CX}
                            cy={DONUT_CY}
                            r={DONUT_R}
                            fill="none"
                            stroke="var(--border)"
                            strokeWidth={STROKE_W}
                        />

                        <circle
                            cx={DONUT_CX}
                            cy={DONUT_CY}
                            r={DONUT_R}
                            fill="none"
                            stroke={hoveredSite ? getUptimeColor(hoveredSite.uptime) : overallColor}
                            strokeWidth={STROKE_W}
                            strokeLinecap="round"
                            strokeDasharray={`${overallDash} ${overallGap}`}
                            strokeDashoffset={CIRC / 4}
                            style={{
                                transition:
                                'stroke-dasharray 1.2s cubic-bezier(.4,0,.2,1), stroke 0.3s ease',
                            }}
                        />

                        <text
                            x={DONUT_CX}
                            y={DONUT_CY - 5}
                            textAnchor="middle"
                            fontSize={13}
                            fontWeight={600}
                            style={{
                                fill: hoveredSite ? getUptimeColor(hoveredSite.uptime) : overallColor,
                                fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                            }}
                        >
                            {(hoveredSite?.uptime ?? overall).toFixed(1)}%
                        </text>
                        <text
                            x={DONUT_CX}
                            y={DONUT_CY + 13}
                            textAnchor="middle"
                            fontSize={9}
                            style={{ fill: 'var(--text-muted)' }}
                        >
                            {hoveredSite ? hoveredSite.siteName.split('.')[0] : `${summary.sitesMonitored} сайтов`}
                        </text>
                    </svg>
                </div>

                <div className="flex-1 flex flex-col gap-2 min-w-0">
                    {sites.slice(0, 5).map(site => {
                        const color = getUptimeColor(site.uptime);
                        const barW = animated ? `${site.uptime}%` : '0%';
                        const isHov = hoveredId === site.siteId;

                        return (
                            <div
                                key={site.siteId}
                                className="flex items-center gap-2 cursor-default group"
                                onMouseEnter={() => setHoveredId(site.siteId)}
                                onMouseLeave={() => setHoveredId(null)}
                            >
                                <div
                                    className="w-1.5 h-1.5 rounded-full flex-shrink-0 transition-transform duration-150"
                                    style={{
                                        backgroundColor: color,
                                        transform: isHov ? 'scale(1.4)' : 'scale(1)',
                                    }}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-0.5">
                                        <span
                                            className="text-[10px] truncate transition-colors duration-150"
                                            style={{ color: isHov ? 'var(--text)' : 'var(--text-secondary)' }}
                                        >
                                            {site.siteName}
                                        </span>
                                        <span
                                            className="text-[10px] font-mono flex-shrink-0 pl-1 tabular-nums"
                                            style={{ color: isHov ? color : 'var(--text-muted)' }}
                                        >
                                            {site.uptime.toFixed(2)}%
                                        </span>
                                    </div>
                                    <div className="h-1 rounded-full bg-[var(--border)] overflow-hidden">
                                        <motion.div
                                            className="h-full rounded-full"
                                            initial={{ width: 0 }}
                                            animate={{ width: barW }}
                                            transition={{ duration: 0.9, delay: 0.15, ease: 'easeOut' }}
                                            style={{ backgroundColor: color }}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {sites.length > 5 && (
                        <span className="text-[10px] text-[var(--text-muted)] pl-3.5">
                            +{sites.length - 5} ещё
                        </span>
                    )}
                </div>
            </div>
        </motion.div>
    );
};